import { createFileRoute } from "@tanstack/react-router";

import type { StudySession } from "@/components/studyos";
import { EngineStoreProvider } from "@/components/studyos";
import { BlockActionContext } from "@/components/studyos/engine-v2/store/block-action-context";

import { HarnessChat } from "./-components/harness-chat";

export const Route = createFileRoute("/preview/agent-harness")({
	component: RouteComponent,
});

const session = {
	id: "harness-mock",
	blocks: [
		{
			id: "intro",
			elementKey: "intro",
			instruction: null,
			type: "primitive-content",
			props: {
				content: [
					{
						type: "markdown",
						markdown:
							"# Agent Harness\n\nThis is a mock study session used by the preview harness.",
					},
				],
			},
			state: {},
			validation: { type: "none" },
			status: "pending",
			attempts: [],
		},
		{
			id: "q1",
			elementKey: "q1",
			instruction: {
				content: [{ type: "text", text: "What is 2 + 2?" }],
			},
			type: "multiple-choice",
			props: {
				options: [
					{ id: "a", option: { content: [{ type: "text", text: "3" }] } },
					{ id: "b", option: { content: [{ type: "text", text: "4" }] } },
					{ id: "c", option: { content: [{ type: "text", text: "5" }] } },
				],
				validation: { type: "single-choice", correctOptionId: "b" },
			},
			state: { selectedId: null },
			validation: { type: "single-choice", correctOptionId: "b" },
			status: "pending",
			attempts: [],
		},
		{
			id: "q2",
			elementKey: "q2",
			instruction: {
				content: [{ type: "text", text: "Fill in the blank." }],
			},
			type: "fill-in-blank",
			props: {
				segments: [
					{ type: "text", content: "The capital of Japan is " },
					{ type: "blank", kind: "text", id: "blank-0" },
					{ type: "text", content: "." },
				],
				validation: {
					type: "fill-in-blank",
					validationType: "strict",
					blanks: [{ blankId: "blank-0", acceptedValues: ["Tokyo"] }],
				},
			},
			state: { answers: { "blank-0": "" } },
			validation: {
				type: "fill-in-blank",
				validationType: "strict",
				blanks: [{ blankId: "blank-0", acceptedValues: ["Tokyo"] }],
			},
			status: "pending",
			attempts: [],
		},
	],
	config: {
		mode: "preview",
		allowBackward: true,
		allowSkip: false,
		autoAdvanceOnCorrect: true,
		autoAdvanceDelay: 1500,
		maxAttempts: 0,
		showProgress: true,
		progressVariant: "steps",
		direction: "horizontal",
	},
} as const satisfies StudySession;

/**
 * Layout: chat transcript on the left (composer pinned at its bottom),
 * block artifact + agent widget strip + student Check/Next bar stacked
 * on the right. <DecorationLayer> floats above everything for annotate
 * callouts; inline decorations (highlight / strike / animate) are applied
 * by DecoratedElement at render time and need no overlay.
 *
 * Everything sits inside <DirectorHost>, which owns useChat and exposes
 * messages / widgets / decorations / send callbacks via DirectorContext.
 */
function RouteComponent() {
	return (
		<BlockActionContext.Provider value={null}>
			<EngineStoreProvider session={session}>
				<div className="relative mx-auto grid h-screen w-full max-w-6xl grid-cols-[1fr_22rem] gap-4 p-4">
					<div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
						<HarnessChat />
					</div>
				</div>
			</EngineStoreProvider>
		</BlockActionContext.Provider>
	);
}
